import { Input, SegmentedControl } from '@popsure/dirty-swan';
import {
  QuestionnaireFormProps,
  QuestionnaireQuestions,
} from '../../../models';
import { useEffect, useState } from 'react';
import { Header } from '../../Screen';

import styles from './NumericConversionInputForm.module.scss';
import {
  centimetersToFeet,
  feetToCentimeters,
  feetToMeters,
  kgToLbs,
  lbsToKg,
  metersToFeet
} from './utils';

type Conversion =
  | {
      type: 'Single';
      fns: [(n: number) => number, (n: number) => number];
    }
  | {
      type: 'Double';
      fns: [(n: number) => [number, number], (n: number, m: number) => number];
    };

const defaultConversion: Conversion = {
  type: 'Single',
  fns: [(n: number) => n, (n: number) => n],
};

const getPreDefinedConversion = (
  type?: NumericConversionType,
  initialConversion?: Conversion
): Conversion => {
  if (type === 'SIZE') {
    return {
        type: 'Double',
        fns: [centimetersToFeet, feetToCentimeters],
    };
  } 
  
  if (type === 'WEIGHT') {
    return {
        type: 'Single',
        fns: [kgToLbs, lbsToKg],
    };
  }

  if (type === 'AREA') {
    return {
        type: 'Single',
        fns: [metersToFeet, feetToMeters],
    };
  }

  return initialConversion || defaultConversion;
}

const getPreDefinedUnitLabels = (
  type?: NumericConversionType,
  initialUnitLabels?: [string, string]
): [string, string] => {
  if (type === 'SIZE') {
    return ['cm', 'feet'];
  } 
  
  if (type === 'WEIGHT') {
    return ['kg', 'lbs'];
  }

  if (type === 'AREA') {
    return ['m²', 'sq. feet'];
  }

  return initialUnitLabels ?? ['', ''];
}

const getPreDefinedPlaceholdersPerUnit = (
  type?: NumericConversionType,
  initialPlaceholders?: [string, string, string?]
): [string, string, string?] => {
  if (type === 'SIZE') {
    return ['Cm', 'Feet', 'Inches'];
  } 
  
  if (type === 'WEIGHT') {
    return ['Kg', 'Lbs'];
  }

  if (type === 'AREA') {
    return ['M²', 'Sq. feet'];
  }

  return initialPlaceholders ?? ['', ''];
}

const formatToTwoDigits = (input: number) =>
  input.toFixed(2).replace(/\.00$/, '');

export type NumericConversionType = 'AREA' | 'WEIGHT' | 'SIZE';

export type NumericConversionInputFormProps<
  Questionnaire extends QuestionnaireQuestions
> = {
  validate?: (value: number) => boolean;
  unitLabels?: [string, string];
  placeholderPerUnit?: [string, string, string?];
  description?: string;
  conversion?: Conversion;
  type?: NumericConversionType;
} & QuestionnaireFormProps<Questionnaire, number>;

export const NumericConversionInputForm = <
  Questionnaire extends QuestionnaireQuestions
>({
  value: defaultValue,
  question,
  onSubmitValue,
  featureName,
  unitLabels: initialUnitLabels,
  placeholder,
  placeholderPerUnit: initialPlaceholdersPerUnit,
  validate,
  setValidForSubmission,
  conversion: initialConversion,
  type,
}: NumericConversionInputFormProps<Questionnaire>) => {
  const [value, setValue] = useState(defaultValue);
  const [selectedUnit, setSelectedUnit] = useState(0);

  const showPrimary = selectedUnit === 0;

  const conversion = getPreDefinedConversion(type, initialConversion);
  const unitLabels = getPreDefinedUnitLabels(type, initialUnitLabels);
  const placeholderPerUnit = getPreDefinedPlaceholdersPerUnit(type, initialPlaceholdersPerUnit);

  const getDisplayValue = () => {
    const isSingleForm = conversion.type === 'Single';
    if (value === undefined) {
      return isSingleForm ? undefined : [];
    }
    if (showPrimary) {
      return formatToTwoDigits(value);
    }

    if (conversion.type === 'Single') {
      return formatToTwoDigits(conversion.fns[0](value));
    }

    const result = conversion.fns[0](value);
    return [formatToTwoDigits(result[0]), formatToTwoDigits(result[1])];
  };

  const displayValue = getDisplayValue();

  const handleSubmit = () => {
    if (!value) {
      throw new Error(
        `[${featureName}] Numeric value is missing for submission for question: ${question}.
        '${unitLabels[0]}': '${value}'`
      );
    }
    onSubmitValue(Number(value.toFixed(2)));
  };

  useEffect(() => {
    const isValid = value && (validate ? validate(value) : true);
    setValidForSubmission(Boolean(isValid));
  }, [value, setValidForSubmission, validate]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const n = Number(e.target.value);
    if (showPrimary) {
      setValue(n);
    } else if (conversion.type === 'Single') {
      setValue(conversion.fns[1](n));
    }
  };

  const handleSecondaryChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    position = 0
  ) => {
    const n = Number(e.target.value);
    if (conversion.type === 'Double') {
      const [a, b] = conversion.fns[0](value ?? 0);
      setValue(
        conversion.fns[1](position === 0 ? n : a, position === 1 ? n : b)
      );
    }
  };

  const placeholderStr = placeholder || placeholderPerUnit?.[selectedUnit];

  if (conversion.type === 'Double') {
    return (
      <Header onSubmit={handleSubmit}>
        <div className="d-flex gap8 w100">
          {showPrimary ? (
            <Input
              className="w100 wmx4"
              placeholder={placeholderStr}
              onChange={handleChange}
              type="number"
              value={displayValue ?? ''}
              pattern="[0-9]*"
              data-cy="input-single-one"
            />
          ) : (
            <>
              <Input
                className="w100 wmx2 mr8"
                placeholder={placeholderPerUnit?.[1]}
                onChange={(e) => handleSecondaryChange(e, 0)}
                type="number"
                value={String(displayValue?.[0])}
                pattern="[0-9]*"
                data-cy="input-double-one"
              />
              <Input
                className="w100 wmx2"
                placeholder={placeholderPerUnit?.[2]}
                onChange={(e) => handleSecondaryChange(e, 1)}
                type="number"
                value={String(displayValue?.[1])}
                pattern="[0-9]*"
                data-cy="input-double-two"
              />
            </>
          )}
          <SegmentedControl
            className={styles.units}
            onChange={(n) => setSelectedUnit(n)}
            values={unitLabels}
            selectedIndex={showPrimary ? 0 : 1}
          />
        </div>
      </Header>
    );
  }

  return (
    <Header onSubmit={handleSubmit}>
      <div className="d-flex gap8 w100">
        <Input
          className="w100 wmx4"
          placeholder={placeholderStr}
          onChange={handleChange}
          type="number"
          value={displayValue ?? ''}
          pattern="[0-9]*"
          data-cy="input-single-one"
        />
        <SegmentedControl
          className={styles.units}
          values={unitLabels}
          selectedIndex={showPrimary ? 0 : 1}
          onChange={(n) => setSelectedUnit(n)}
        />
      </div>
    </Header>
  );
};
